import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import { parseISO } from 'date-fns/parseISO';
import { type Registry as Services, service } from '@ember/service';
import { isBefore } from 'date-fns/isBefore';
import { addHours } from 'date-fns/addHours';
import { isSameDay } from 'date-fns/isSameDay';
import { isAfter } from 'date-fns/isAfter';
import { startOfDay } from 'date-fns/startOfDay';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import {
  nightsBetween,
  parseLocalDay,
  utcDate,
} from 'uplisting-frontend/utils/date';
import { getPreferredMessagingChannel } from 'uplisting-frontend/utils/messaging-channels';
import { channelIcon } from 'uplisting-frontend/utils/channel-icon';
import { propertyDisplayName } from 'uplisting-frontend/utils/property';

export enum BookingStatus {
  accepted = 'accepted',
  approved = 'approved',
  cancelled = 'cancelled',
  denied = 'denied',
  rejected = 'rejected',
  timedout = 'timedout',
  externallyRemoved = 'externally_removed',
  booked = 'booked',
  notPossible = 'not_possible',
  declined = 'declined',
  active = 'active',
  needsCheckOut = 'needs_check_out',
  needsCheckIn = 'needs_check_in',
  expired = 'expired',
  checkedIn = 'checked_in',
  checkedOut = 'checked_out',
  pending = 'pending',
  confirmed = 'confirmed',
}

export enum BookingSource {
  apartmentsOnline = 'apartments_online',
  bookingSiteConversion = 'booking_site_conversion',
  email = 'email',
  extension = 'extension',
  livinc = 'livinc',
  openRent = 'open_rent',
  phone = 'phone',
  referral = 'referral',
  silverDoor = 'silver_door',
  situ = 'situ',
  vrbo = 'vrbo',
  website = 'website',
}

export enum SafeguestVerificationStatus {
  pending = 'pending',
  green = 'green',
  yellow = 'yellow',
  red = 'red',
}

export enum BookingChannel {
  agoda = 'agoda',
  airbnb = 'airbnb',
  airbnbOfficial = 'airbnb_official',
  arendoo = 'arendoo',
  bookingDotCom = 'booking_dot_com',
  canadaStays = 'canada_stays',
  canopyAndStars = 'canopy_and_stars',
  expedia = 'expedia',
  ferieboligkbh = 'ferieboligkbh',
  google = 'google',
  homeAway = 'home_away',
  lodgify = 'lodgify',
  misterbandb = 'misterbandb',
  nineFlats = 'nine_flats',
  oneDay = 'one_day',
  oneRooftop = 'one_rooftop',
  partnerIcal = 'partner_ical',
  plumGuide = 'plum_guide',
  selectCornwall = 'select_cornwall',
  tripAdvisor = 'trip_advisor',
  uplisting = 'uplisting',
  uplistingIcal = 'uplisting_ical',
  vrbo = 'vrbo',
  wimdu = 'wimdu',
  wpRentals = 'wp_rentals',
}

const MOVEABLE_CHANNELS = [
  BookingChannel.airbnbOfficial,
  BookingChannel.bookingDotCom,
  BookingChannel.google,
  BookingChannel.homeAway,
  BookingChannel.uplisting,
];

const CHANNELS_WITH_PAYMENTS = [
  BookingChannel.bookingDotCom,
  BookingChannel.google,
  BookingChannel.homeAway,
  BookingChannel.uplisting,
];

const PRICE_EDITABLE_CHANNELS = [
  BookingChannel.airbnbOfficial,
  BookingChannel.bookingDotCom,
  BookingChannel.google,
  BookingChannel.uplisting,
  BookingChannel.homeAway,
  BookingChannel.partnerIcal,
];

export interface ISafeguestVerification {
  status: SafeguestVerificationStatus;
  guest_link: string;
}

export default class BookingModel extends Model {
  @service translations!: Services['translations'];

  @service('calendar/manager')
  calendarManager!: Services['calendar/manager'];

  @attr('string') arrivalTime!: string;
  @attr('string') channel!: BookingChannel;
  @attr('string') checkIn!: string;
  @attr('string') checkOut!: string;
  @attr('string') departureTime!: string;
  @attr('string') guestName!: string;
  @attr('string') rawGuestName!: string;
  @attr('string') guestPhone!: string;
  @attr('string') status!: BookingStatus;
  @attr('string') guestEmail!: string;
  @attr('string') channelName!: string;
  @attr('string') preferredGuestName!: string;
  @attr('boolean') imported!: boolean;
  @attr('boolean') deletable!: boolean;
  @attr('date') createdAt!: Date;
  @attr('date') bookedAt!: Date;
  @attr('string') note!: string;
  @attr('string') externalReservationId!: string;
  @attr('string') externalReservationLink!: string;
  @attr('boolean') viaApi!: boolean;
  @attr('boolean') automatedMessagesEnabled!: boolean;
  @attr('boolean') automatedReviewsEnabled!: boolean;
  @attr('boolean') automatedReviewSent!: boolean;
  @attr('boolean') problematic!: boolean;
  @attr('number') expiresIn!: number;
  @attr('string') bookingConfirmationPageUrl!: string;
  @attr('boolean') lockCodeEditable!: boolean;
  @attr('string') customLockCode!: string | null;
  @attr('boolean') direct!: boolean;
  @attr('string') source!: BookingSource;
  @attr('object') safeguestVerification?: ISafeguestVerification;
  @attr('number') numberOfAdults!: number | null;
  @attr('number') numberOfChildren!: number | null;
  @attr('number') numberOfInfants!: number | null;
  @attr('number') numberOfPets!: number | null;

  get hasGuestBreakdown(): boolean {
    return [
      this.numberOfAdults,
      this.numberOfChildren,
      this.numberOfInfants,
      this.numberOfPets,
    ].some((count) => (count ?? 0) > 0);
  }

  @cached
  get displayChannel(): string {
    if (this.channel) {
      return this.translations.getChannelNameFor(this.channel);
    }

    return this.channelName;
  }

  @cached
  get displayGuestName(): string {
    return this.preferredGuestName ?? this.guestName;
  }

  @cached
  get displayName(): string {
    return propertyDisplayName(this.property, this.multiUnit);
  }

  @cached
  get isGoogle(): boolean {
    return this.channel === BookingChannel.google;
  }

  @cached
  get isUplisting(): boolean {
    return this.channel === BookingChannel.uplisting;
  }

  @cached
  get isAirbnbOfficial(): boolean {
    return this.channel === BookingChannel.airbnbOfficial;
  }

  @cached
  get isVrbo(): boolean {
    return this.channel === BookingChannel.homeAway && this.viaApi;
  }

  @cached
  get channelIcon(): string | undefined {
    return channelIcon(this.channel);
  }

  @cached
  get isBookingDotCom(): boolean {
    return this.channel === BookingChannel.bookingDotCom;
  }

  @cached
  get isIcal(): boolean {
    if (this.viaApi) {
      return false;
    }

    return !(this.isUplisting || this.isGoogle);
  }

  @cached
  get isPending(): boolean {
    return this.status === BookingStatus.pending;
  }

  @cached
  get isCancelled(): boolean {
    return this.status === BookingStatus.cancelled;
  }

  @cached
  get duration(): number {
    return nightsBetween(this.checkIn, this.checkOut);
  }

  @cached
  get startDate(): Date {
    return new Date(this.checkIn);
  }

  @cached
  get endDate(): Date {
    return new Date(this.checkOut);
  }

  @cached
  get currency(): string {
    return this.price.currency ?? this.property.currency;
  }

  @cached
  get messagesSorted(): ModelRegistry['message'][] {
    return this.messages.slice().sort((x, y) => {
      if (isBefore(x.createdAt, y.createdAt)) {
        return -1;
      } else if (isBefore(y.createdAt, x.createdAt)) {
        return 1;
      }

      return 0;
    });
  }

  // when changing dates on the booking price page, we call `changeset.execute()`
  // in order to apply new dates and show new pricing information
  // but that will change booking duration on the calendar as well
  // adding those getters here, to preserve booking duration until booking is saved
  // but also, when booking is being drag and dropped, or resized, we want to show that immediately
  @cached
  get persistedStartDate(): Date {
    if (this.calendarManager.isStaging(this)) {
      return this.startDate;
    }

    const changedAttributes = this.changedAttributes();

    if (!!this.hasDirtyAttributes && changedAttributes['checkIn']?.[0]) {
      return new Date(changedAttributes['checkIn'][0]);
    }

    return this.startDate;
  }

  @cached
  get persistedEndDate(): Date {
    if (this.calendarManager.isStaging(this)) {
      return this.endDate;
    }

    const changedAttributes = this.changedAttributes();

    if (!!this.hasDirtyAttributes && changedAttributes['checkOut']?.[0]) {
      return new Date(changedAttributes['checkOut'][0]);
    }

    return this.endDate;
  }

  @cached
  get checkInDate(): Date {
    return parseLocalDay(this.checkIn);
  }

  @cached
  get checkOutDate(): Date {
    return parseLocalDay(this.checkOut);
  }

  @cached
  get expiresInTime(): Date {
    return addHours(this.createdAt, this.expiresIn);
  }

  @cached
  get isCheckedIn(): boolean {
    return this.status === BookingStatus.checkedIn;
  }

  @cached
  get isCheckedOut(): boolean {
    return this.status === BookingStatus.checkedOut;
  }

  @cached
  get needsCheckIn(): boolean {
    return this.status === BookingStatus.needsCheckIn;
  }

  @cached
  get needsCheckOut(): boolean {
    return this.status === BookingStatus.needsCheckOut;
  }

  @cached
  get isCheckOutToday(): boolean {
    return isSameDay(this.checkOutDate, utcDate(startOfDay(new Date())));
  }

  @cached
  get isCheckOutInFuture(): boolean {
    return isAfter(this.checkOutDate, utcDate(startOfDay(new Date())));
  }

  @cached
  get isManual(): boolean {
    return this.isUplisting && !this.direct && !this.viaApi;
  }

  @cached
  get isMoveable(): boolean {
    return MOVEABLE_CHANNELS.includes(this.channel);
  }

  @cached
  get isPayable(): boolean {
    return CHANNELS_WITH_PAYMENTS.includes(this.channel);
  }

  @cached
  get isPriceEditable(): boolean {
    const { price } = this;

    if (!price) {
      return false;
    }

    return PRICE_EDITABLE_CHANNELS.includes(this.channel) && price.visible;
  }

  @cached
  get isEditable(): boolean {
    return this.isUplisting || this.isGoogle || this.isVrbo;
  }

  @cached
  get pendingAlterationRequest():
    | ModelRegistry['airbnb-official-alteration-request']
    | undefined {
    return this.airbnbOfficialAlterationRequests?.find(
      (request) => request.isPending,
    );
  }

  @cached
  get acceptedAlterationRequest():
    | ModelRegistry['airbnb-official-alteration-request']
    | undefined {
    return this.airbnbOfficialAlterationRequests?.find(
      (request) => request.isAccepted,
    );
  }

  @cached
  get pendingAlterationRequestByGuest(): boolean {
    return !!this.pendingAlterationRequest?.changeRequestedByGuest;
  }

  @cached
  get pendingAlterationRequestByHost(): boolean {
    return !!this.pendingAlterationRequest?.changeRequestedByHost;
  }

  @cached
  get canBeModified(): boolean {
    return (
      this.isAirbnbOfficial &&
      this.property.hasConfirmedAirbnbLink &&
      !this.pendingAlterationRequest &&
      this.isCheckOutInFuture
    );
  }

  @cached
  get canBeAirbnbCancelled(): boolean {
    return (
      this.isAirbnbOfficial &&
      this.property.hasConfirmedAirbnbLink &&
      this.isCheckOutInFuture
    );
  }

  @cached
  get hasChangedBookingDates(): boolean {
    if (!this.pendingAlterationRequest) {
      return false;
    }

    const { startDate, endDate } = this.pendingAlterationRequest;

    return (
      !startDate ||
      !endDate ||
      !isSameDay(this.checkInDate, parseISO(startDate)) ||
      !isSameDay(this.checkOutDate, parseISO(endDate))
    );
  }

  @cached
  get hasChangedBookingGuests(): boolean {
    if (!this.pendingAlterationRequest) {
      return false;
    }

    return (
      this.price.numberOfGuests !== this.pendingAlterationRequest.numberOfGuests
    );
  }

  @belongsTo('changeover', { inverse: 'booking', async: false })
  changeover?: ModelRegistry['changeover'];

  @belongsTo('occasion', { inverse: null, async: false })
  occasion!: ModelRegistry['occasion'];

  @belongsTo('ai-occasion-context', { inverse: null, async: false })
  aiOccasionContext?: ModelRegistry['ai-occasion-context'];

  @belongsTo('property', { inverse: 'bookings', async: false })
  property!: ModelRegistry['property'];

  @belongsTo('property', { inverse: null, async: false })
  newProperty?: ModelRegistry['property'];

  @belongsTo('calendar', { inverse: 'bookings', async: false })
  calendar!: ModelRegistry['calendar'];

  @belongsTo('price', { inverse: 'booking', async: false })
  price!: ModelRegistry['price'];

  @belongsTo('multi-unit', { inverse: 'bookings', async: false })
  multiUnit?: ModelRegistry['multi-unit'];

  @belongsTo('multi-unit', { inverse: null, async: false })
  newMultiUnit?: ModelRegistry['multi-unit'];

  @belongsTo('guest-identity-verification', {
    inverse: 'booking',
    async: false,
  })
  guestIdentityVerification?: ModelRegistry['guest-identity-verification'];

  @belongsTo('channel-user', { async: false, inverse: null })
  channelUser!: ModelRegistry['channel-user'];

  @belongsTo('booking-payment', { async: false, inverse: 'booking' })
  bookingPayment?: ModelRegistry['booking-payment'];

  @belongsTo('lock-code', { async: false, inverse: 'booking' })
  lockCode?: ModelRegistry['lock-code'];

  @belongsTo('security-deposit', { async: false, inverse: 'booking' })
  securityDeposit!: ModelRegistry['security-deposit'];

  @belongsTo('homeaway-action', { async: false, inverse: 'booking' })
  homeawayAction!: ModelRegistry['homeaway-action'];

  @belongsTo('booking-dot-com-action', { async: false, inverse: 'booking' })
  bookingDotComAction!: ModelRegistry['booking-dot-com-action'];

  @belongsTo('booking-payment-action', { async: false, inverse: 'booking' })
  bookingPaymentAction!: ModelRegistry['booking-payment-action'];

  @belongsTo('security-deposit-action', { async: false, inverse: 'booking' })
  securityDepositAction!: ModelRegistry['security-deposit-action'];

  @belongsTo('booking-rental-agreement', { async: false, inverse: 'booking' })
  bookingRentalAgreement!: ModelRegistry['booking-rental-agreement'];

  @belongsTo('booking-rental-agreement-action', {
    async: false,
    inverse: 'booking',
  })
  bookingRentalAgreementAction!: ModelRegistry['booking-rental-agreement-action'];

  @belongsTo('guest-identity-verification-action', {
    async: false,
    inverse: 'booking',
  })
  guestIdentityVerificationAction!: ModelRegistry['guest-identity-verification-action'];

  @belongsTo('booking-management-fee-rule', {
    async: false,
    inverse: 'booking',
  })
  managementFeeRule!: ModelRegistry['booking-management-fee-rule'];

  @belongsTo('booking-management-fee-setting', {
    async: false,
    inverse: 'booking',
  })
  bookingManagementFeeSetting?: ModelRegistry['booking-management-fee-setting'];

  @belongsTo('airbnb-official-booking', { async: false, inverse: 'booking' })
  airbnbOfficialBooking!: ModelRegistry['airbnb-official-booking'];

  @belongsTo('inbox-message', { async: false, inverse: 'bookings' })
  inboxMessage!: ModelRegistry['inbox-message'];

  @hasMany('message', { async: false, inverse: 'booking' })
  messages!: ModelRegistry['message'][];

  @hasMany('messaging-channel', { async: false, inverse: 'booking' })
  messagingChannels!: ModelRegistry['messaging-channel'][];

  @cached
  get preferredMessagingChannel(): string {
    return getPreferredMessagingChannel(this.channel, this.messagingChannels);
  }

  @hasMany('scheduled-message', { inverse: 'booking', async: false })
  scheduledMessages!: ModelRegistry['scheduled-message'][];

  @hasMany('guest-invoice', { inverse: 'booking', async: false })
  guestInvoices!: ModelRegistry['guest-invoice'][];

  @hasMany('balance-adjustment', { inverse: 'booking', async: false })
  balanceAdjustments!: ModelRegistry['balance-adjustment'][];

  @hasMany('history-booking-price', { inverse: null, async: false })
  priceChanges!: ModelRegistry['history-booking-price'][];

  @hasMany('additional-booking-charge', { inverse: null, async: false })
  pendingAdditionalCharges!: ModelRegistry['additional-booking-charge'][];

  @hasMany('airbnb-official-alteration-request', {
    async: false,
    inverse: 'booking',
  })
  airbnbOfficialAlterationRequests!: ModelRegistry['airbnb-official-alteration-request'][];

  @hasMany('dispute', { inverse: null, async: false })
  disputes!: ModelRegistry['dispute'][];
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    booking: BookingModel;
  }
}
